import { toast } from "react-toastify";
import { FINANCIAL_KPI_PATHS, FinancialKpiData, FinancialKpiSection, KpiSnapshot } from "models/financial-kpi";

// API only: the page dispatches the slice actions after these resolve.
const API = `${process.env.REACT_APP_API_URL}/financial-kpi`;

const errorMessage = async (res: Response, fallback: string) => {
    const body = await res.json().catch(() => null);
    return body?.message ?? fallback;
};

// One container of the screen for a fiscal year.
export const getFinancialKpiSection = async <K extends FinancialKpiSection>(section: K, fiscalYear: number): Promise<FinancialKpiData[K] | null> => {
    try {
        const res = await fetch(`${API}/${FINANCIAL_KPI_PATHS[section]}?fiscalYear=${fiscalYear}`, { credentials: "include" });
        if (!res.ok) throw new Error(await errorMessage(res, "Error loading Financial KPI Metrics."));
        return (await res.json()).data as FinancialKpiData[K];
    } catch (err: any) {
        toast.error(err.message ?? "Error loading Financial KPI Metrics.");
        return null;
    }
};

// Freezes the live funnel and next-year WIH for the current month (replaces this month's snapshot).
export const saveFinancialKpiSnapshot = async (): Promise<KpiSnapshot | null> => {
    try {
        const res = await fetch(`${API}/snapshot`, { method: "POST", credentials: "include" });
        if (!res.ok) throw new Error(await errorMessage(res, "Error saving the snapshot."));
        toast.success("Snapshot saved.");
        return (await res.json()).data as KpiSnapshot;
    } catch (err: any) {
        toast.error(err.message ?? "Error saving the snapshot.");
        return null;
    }
};

export const downloadFinancialKpiExport = async (fiscalYear: number): Promise<boolean> => {
    try {
        const res = await fetch(`${API}/export?fiscalYear=${fiscalYear}`, { credentials: "include" });
        if (!res.ok) throw new Error(await errorMessage(res, "Error downloading the export."));

        const blob = await res.blob();
        const blobUrl = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = blobUrl;
        a.download = `financial-kpi-metrics-FY${fiscalYear}.xlsx`;
        a.click();
        window.URL.revokeObjectURL(blobUrl);
        return true;
    } catch (err: any) {
        toast.error(err.message ?? "Error downloading the export.");
        return false;
    }
};
